/**
 * ManagerSignatureModal Component
 * 
 * Modal that opens automatically when a store reaches 100% completion.
 * Allows store manager (or operator) to sign off the store visit.
 */

import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SignaturePad } from '@/components/mobile/SignaturePad';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { ModalContextHeader } from '@/components/mobile/ModalContextHeader';

interface ManagerSignatureModalProps {
  open: boolean;
  onClose: () => void;
  storeId: number | null;
  storeName: string | null;
  operatorId?: number;
  cycleId?: number;
}

export function ManagerSignatureModal({ 
  open, 
  onClose, 
  storeId,
  storeName,
  operatorId,
  cycleId
}: ManagerSignatureModalProps) {
  const [signature, setSignature] = useState<string | null>(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [isSubmittingLocal, setIsSubmittingLocal] = useState(false); // ✅ FIX #1: Double-click prevention
  const [showSuccess, setShowSuccess] = useState(false); // ✅ FIX #3: Success animation
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  // ✅ PROSCRIPTIVE WORKFLOW: Precondition check - verify store is 'completed' before signature
  useEffect(() => {
    if (open && storeId && operatorId && cycleId) {
      const checkStatus = async () => {
        try {
          const response = await apiFetch(`/api/mobile/store-signoff/status/${storeId}?operatorId=${operatorId}&cycleId=${cycleId}`);
          if (response.ok) {
            const status = await response.json();
            
            // Check if already signed off
            if (status.isSignedOff) {
              console.log('[SIGNOFF_PREFLIGHT] Store already signed off - closing modal', { storeId, signedAt: status.signedAt });
              toast({
                title: 'Store Already Signed Off',
                description: `${storeName || 'Store'} was signed off at ${new Date(status.signedAt).toLocaleTimeString()}`,
              });
              onClose();
              
              // Force hard cache invalidation
              queryClient.removeQueries({ queryKey: ['completion'] });
              queryClient.removeQueries({ queryKey: ['route-overview'] });
              queryClient.refetchQueries({ queryKey: ['completion'] });
              queryClient.refetchQueries({ queryKey: ['route-overview'] });
              return;
            }
            
            // ✅ PROSCRIPTIVE WORKFLOW: Check store status is 'completed'
            if (status.status && status.status !== 'completed') {
              console.warn('[SIGNOFF_PREFLIGHT] Store not ready for signature', { 
                storeId, 
                currentStatus: status.status,
                expectedStatus: 'completed'
              });
              toast({
                title: 'Store not ready',
                description: `Mark store complete before capturing signature. Current status: ${status.status}`,
                variant: 'destructive',
              });
              onClose();
              return;
            }
          }
        } catch (error) {
          console.error('[SIGNOFF_PREFLIGHT] Failed to check status', error);
          // Continue anyway - user can try to sign off
        }
      };
      checkStatus();
    }
  }, [open, storeId, operatorId, cycleId, storeName, onClose, queryClient, toast]);

  // Reset signature when modal opens/closes
  useEffect(() => {
    if (!open) {
      setSignature(null);
      setShowSignaturePad(false);
      setIsSubmittingLocal(false);
      setShowSuccess(false);
    }
  }, [open]);

  const { mutate: submitSignature, isPending: isLoading } = useMutation({
    mutationFn: async (signatureData: string) => {
      if (!storeId) {
        throw new Error('Store ID is required');
      }

      const response = await apiFetch(`/api/mobile/store-signoff/${storeId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          signature: signatureData,
          operatorId,
          cycleId, // ✅ CRITICAL: Include cycleId to filter for current cycle's store_visit
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle 409 Conflict (already signed off)
        if (response.status === 409) {
          throw new Error(errorData.message || 'This store has already been signed off');
        }
        
        throw new Error(errorData.message || errorData.error || 'Failed to submit signature');
      }

      return response.json();
    },
    onSuccess: async () => {
      // ✅ FIX #3: Show success animation first
      setShowSuccess(true);
      setIsSubmittingLocal(false);
      
      toast({
        title: 'Store Signed Off',
        description: `${storeName || 'Store'} has been signed off successfully.`,
      });
      
      // ✅ IMMEDIATE REFETCH: Ensure data is fresh before navigation
      console.log('[SIGNOFF] Refetching all data before navigation...');
      
      try {
        // ✅ HARD CACHE INVALIDATION: Remove stale data first, then refetch
        console.log('[SIGNOFF] Removing cached queries for hard refresh');
        queryClient.removeQueries({ queryKey: ['route-overview'] });
        queryClient.removeQueries({ queryKey: ['storeVisits'] });
        queryClient.removeQueries({ queryKey: ['completion'] });
        queryClient.removeQueries({ queryKey: ['cycleSummary'] });
        
        // Refetch ALL queries in parallel to get fresh data immediately
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ['route-overview'] }),
          queryClient.refetchQueries({ queryKey: ['storeVisits'] }),
          queryClient.refetchQueries({ queryKey: ['completion'] }),
          queryClient.refetchQueries({ 
            queryKey: ['cycleSummary'],
            exact: false
          })
        ]);
        
        console.log('[SIGNOFF] Data refetch complete');
        
        // Wait 1 second to show success animation
        setTimeout(() => {
          onClose();
          
          // Check if entire cycle is now complete before navigating to completion page
          if (cycleId && operatorId) {
            // Navigate based on cycle status
            queryClient.refetchQueries({ 
              queryKey: ['cycleSummary', operatorId, cycleId]
            }).then((result: any) => {
              const cycleSummary = result?.[0]?.data as any;
              
              if (cycleSummary?.status === 'signed_off') {
                console.log(`[SIGNOFF_NAVIGATION] All stores signed off - navigating to cycle completion page`);
                setLocation(`/cycle/${cycleId}/complete`);
              } else {
                console.log(`[SIGNOFF_NAVIGATION] Cycle still in progress (${cycleSummary?.signedStores}/${cycleSummary?.totalStores} stores) - returning to route`);
                setLocation('/route-overview');
              }
            });
          } else {
            // No cycleId - just go back to route overview
            setLocation('/route-overview');
          }
        }, 1000); // 1 second success animation
        
      } catch (error) {
        console.error('[SIGNOFF] Failed to refetch data or check cycle status', error);
        // Fallback: invalidate and navigate anyway
        queryClient.invalidateQueries({ queryKey: ['route-overview'] });
        queryClient.invalidateQueries({ queryKey: ['storeVisits'] });
        queryClient.invalidateQueries({ queryKey: ['completion'] });
        setTimeout(() => {
          onClose();
          setLocation('/route-overview');
        }, 1000);
      }
    },
    onError: (error: Error) => {
      setIsSubmittingLocal(false); // ✅ Reset submitting state on error
      
      // ✅ SYNC FIX: Handle 409 as a success state - store IS signed off
      if (error.message.includes('already been signed off') || error.message.includes('already signed off')) {
        console.log('[SIGNOFF_ERROR] Duplicate sign-off detected - treating as success and hard invalidating cache');
        
        // Show success toast - the store IS signed off
        toast({
          title: 'Store Already Signed Off',
          description: `${storeName || 'Store'} has already been signed off successfully.`,
        });
        
        // Close modal immediately
        onClose();
        
        // ✅ HARD CACHE INVALIDATION: Remove cached data and refetch
        queryClient.removeQueries({ queryKey: ['completion'] });
        queryClient.removeQueries({ queryKey: ['route-overview'] });
        queryClient.removeQueries({ queryKey: ['storeVisits'] });
        queryClient.refetchQueries({ queryKey: ['completion'] });
        queryClient.refetchQueries({ queryKey: ['route-overview'] });
        queryClient.refetchQueries({ queryKey: ['storeVisits'] });
        
        // Navigate to route overview to show updated state
        setTimeout(() => {
          setLocation('/route-overview');
        }, 300);
        
        return;
      }
      
      // Handle other errors
      toast({
        title: 'Sign-Off Failed',
        description: error.message || 'Failed to submit signature. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const handleSignatureCapture = (dataUrl: string) => {
    setSignature(dataUrl);
    setShowSignaturePad(false);
  };

  const handleSubmit = () => {
    // ✅ FIX #1: Prevent double-click
    if (isSubmittingLocal || isLoading) {
      console.log('[SIGNOFF] Already submitting - ignoring duplicate click');
      return;
    }

    if (!signature) {
      toast({
        title: 'Signature Required',
        description: 'Please provide a signature before submitting.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmittingLocal(true);
    submitSignature(signature);
  };

  if (!storeId || !storeName) {
    return null;
  }

  return (
    <>
      <Dialog open={open && !showSignaturePad} onOpenChange={onClose}>
        <DialogContent className="max-w-md space-y-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Store Complete - Manager Sign-Off
            </DialogTitle>
            <DialogDescription>
              {storeName} is complete. Please sign below to confirm completion.
            </DialogDescription>
            <ModalContextHeader 
              storeName={storeName || 'Unknown Store'}
              // Manager signature is for entire store, not a specific assignment
            />
          </DialogHeader>

          {/* ✅ FIX #3: Success Animation */}
          {showSuccess ? (
            <div className="bg-green-50 p-8 text-center rounded-lg border-2 border-green-200">
              <CheckCircle2 className="h-20 w-20 text-green-600 mx-auto mb-4 animate-bounce" />
              <h3 className="text-xl font-bold text-green-900 mb-2">Store Signed Off!</h3>
              <p className="text-sm text-green-700">{storeName}</p>
            </div>
          ) : (
          <div className="space-y-4">
            {/* Summary */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-green-900">
                  Store Completed
                </span>
                <span className="text-lg font-bold text-green-700">
                  {storeName}
                </span>
              </div>
            </div>

            {/* Signature Preview or Capture Button */}
            {signature ? (
              <div className="space-y-3">
                <div className="border-2 border-gray-300 rounded-lg p-4 bg-white">
                  <img 
                    src={signature} 
                    alt="Signature preview" 
                    className="max-w-full h-auto"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSignature(null);
                    setShowSignaturePad(true);
                  }}
                  className="w-full"
                >
                  Change Signature
                </Button>
              </div>
            ) : (
              <Button
                onClick={() => setShowSignaturePad(true)}
                className="w-full"
                variant="outline"
              >
                Capture Signature
              </Button>
            )}

            {/* ✅ FIX #3: Enhanced Loading State */}
            {(isLoading || isSubmittingLocal) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-blue-600" />
                <p className="text-sm font-medium text-blue-900">Submitting signature...</p>
                <p className="text-xs text-blue-700 mt-1">Please wait</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={isLoading || isSubmittingLocal}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!signature || isLoading || isSubmittingLocal}
                className="flex-1"
              >
                {(isLoading || isSubmittingLocal) ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Confirm Sign-Off
                  </>
                )}
              </Button>
            </div>
          </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Signature Pad Modal - rendered separately to avoid Dialog interference */}
      {showSignaturePad && (
        <SignaturePad
          onSignature={handleSignatureCapture}
          onCancel={() => setShowSignaturePad(false)}
          title="Manager Signature"
          placeholder="Please sign to confirm store completion"
          isSubmitting={isLoading}
        />
      )}
    </>
  );
}

